import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, LogIn, UserPlus, LogOut, ChevronRight, ChevronDown, LayoutDashboard } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import AuthModal from "./AuthModal";
import Avatar from "../common/Avatar";
import { useUser } from "../../context/UserContext";
import { navLinks, announcements } from "../../mock";
import { fetchSettings } from "../../lib/api";
import logo from "../../assets/logo-transparent.png";

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const CREDIT_CONSULTATION_URL =
  "https://pulse.disputeprocess.com/jsp/custom_form.jsp?tab_id=N0JSeGJXNWx5Nll2bU9KR0VVaE5zUT09&add_affiliate=0&cust_type=1&company_id=UW4rYVZtcDExVXRJY1d1QmVXOUVaUT09&isLinkFromIframe=1&redirect_url=";

const CREDIT_SIGNUP_URL =
  "https://pulse.scorexer.com/portal-signUp/signup.jsp?id=Q29zV1BRbmJyZklhTG9ORW5IYkRQdz09";

const CREDIT_CLIENTS_LOGIN_URL = "https://pulse.scorexer.com";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("signup");
  const [marquee, setMarquee] = useState(null);
  const { user, logout } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings()
      .then((s) => setMarquee({ enabled: !!s.marquee_enabled, text: s.marquee_text || "" }))
      .catch(() => setMarquee({ enabled: true, text: "" }));
  }, []);

  // While loading, fall back to default announcements to avoid a flash of empty bar.
  let marqueeLines;
  if (marquee === null) marqueeLines = announcements;
  else if (!marquee.enabled) marqueeLines = [];
  else marqueeLines = marquee.text.trim()
    ? marquee.text.split("\n").map((t) => t.trim()).filter(Boolean)
    : announcements;
  // Support optional "Message | link" syntax per line.
  const marqueeItems = marqueeLines.map((l) => {
    const idx = l.indexOf("|");
    return idx === -1
      ? { text: l, link: "" }
      : { text: l.slice(0, idx).trim(), link: l.slice(idx + 1).trim() };
  });

  const handleMarqueeClick = (link) => {
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.open(link, "_blank", "noopener");
      return;
    }
    handleNav(link);
  };

  const openModal = (tab) => {
    setModalTab(tab);
    setModalOpen(true);
    setOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/" + href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Announcement marquee */}
      {marqueeItems.length > 0 && (
        <div className="bg-navy-deep text-gold-light text-xs sm:text-sm py-2 overflow-hidden whitespace-nowrap" data-testid="site-marquee">
          <div className="inline-flex animate-marquee gap-12 pl-4">
            {[...marqueeItems, ...marqueeItems].map((a, i) => (
              <span
                key={i}
                onClick={() => handleMarqueeClick(a.link)}
                className={`inline-flex items-center gap-2 font-medium tracking-wide ${a.link ? "cursor-pointer hover:text-gold hover:underline" : ""}`}
                data-testid={a.link ? "marquee-link" : undefined}
              >
                <span className="text-gold">✦</span> {a.text}
              </span>
            ))}
          </div>
        </div>
      )}

      <nav
        className={`transition-all duration-300 ${
          scrolled ? "bg-navy/95 backdrop-blur shadow-lg py-2" : "bg-navy py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center group">
            <img
              src={logo}
              alt="Rehabilitated 4 Life"
              className="h-16 sm:h-20 w-auto bg-white rounded-lg px-2 py-1 shadow-md group-hover:scale-105 transition-transform"
              data-testid="navbar-logo"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-7">
            {navLinks
              .filter((link) => link.href !== "/terms" && link.label !== "Testimonials")
              .map((link) =>
                link.label === "Credit Resources" ? (
                  <DropdownMenu key={link.label}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="inline-flex items-center gap-1 text-sm text-gray-200 hover:text-gold font-medium transition-colors outline-none"
                        data-testid="nav-credit-resources-dropdown"
                      >
                        {link.label}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      <DropdownMenuItem
                        onClick={() => handleNav(link.href)}
                        className="cursor-pointer"
                      >
                        Credit Resources
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open(CREDIT_CONSULTATION_URL, "_blank", "noopener")}
                        className="cursor-pointer"
                        data-testid="nav-credit-consultation-link"
                      >
                        Credit Consultation
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open(CREDIT_SIGNUP_URL, "_blank", "noopener")}
                        className="cursor-pointer"
                        data-testid="nav-credit-signup-link"
                      >
                        Credit Services Sign Up
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => window.open(CREDIT_CLIENTS_LOGIN_URL, "_blank", "noopener")}
                        className="cursor-pointer"
                        data-testid="nav-credit-clients-login-link"
                      >
                        Credit Clients Login
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <button
                    key={link.label}
                    onClick={() => handleNav(link.href)}
                    className="text-sm text-gray-200 hover:text-gold font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-gold hover:after:w-full after:transition-all"
                  >
                    {link.label}
                  </button>
                )
              )}
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  onClick={() => navigate("/portal")}
                  className="bg-gold hover:bg-gold-dark text-navy font-semibold"
                >
                  <LayoutDashboard className="h-4 w-4 mr-1.5" /> Client Portal
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/10 transition-colors">
                      <Avatar
                        name={user.name}
                        src={user.avatar}
                        className="h-8 w-8 rounded-full bg-navy-light text-gold text-sm font-semibold ring-1 ring-white/20"
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="font-semibold text-navy">{user.name}</div>
                      <div className="text-xs text-gray-500 truncate">{user.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => navigate("/portal")}
                      className="cursor-pointer"
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Customer Portal
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/portal/messages")}
                      className="cursor-pointer"
                    >
                      <LogIn className="h-4 w-4 mr-2" /> Messages
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={logout}
                      className="text-red-600 focus:text-red-600 cursor-pointer"
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  onClick={() => openModal("signup")}
                  className="bg-gold hover:bg-gold-dark text-navy font-semibold"
                >
                  <UserPlus className="h-4 w-4 mr-1.5" /> Create Account / Sign In
                </Button>
              </div>
            )}

            {/* Mobile */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-white p-2">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-navy text-white border-navy-light w-[85vw] max-w-[320px] overflow-y-auto">
                <div className="mt-8 flex flex-col gap-1">
                  {navLinks
                    .filter((link) => link.href !== "/terms" && link.label !== "Testimonials")
                    .map((link) => (
                      <React.Fragment key={link.label}>
                        <button
                          onClick={() => handleNav(link.href)}
                          className="flex items-center justify-between text-left py-3 px-2 text-gray-200 hover:text-gold hover:bg-navy-light rounded-md transition-colors"
                        >
                          {link.label}
                          <ChevronRight className="h-4 w-4" />
                        </button>
                        {link.label === "Credit Resources" && (
                          <>
                            <button
                              onClick={() => { setOpen(false); window.open(CREDIT_CONSULTATION_URL, "_blank", "noopener"); }}
                              className="flex items-center justify-between text-left py-2.5 pl-6 pr-2 text-sm text-gray-300 hover:text-gold hover:bg-navy-light rounded-md transition-colors"
                              data-testid="nav-mobile-credit-consultation-link"
                            >
                              Credit Consultation
                              <ChevronRight className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => { setOpen(false); window.open(CREDIT_SIGNUP_URL, "_blank", "noopener"); }}
                              className="flex items-center justify-between text-left py-2.5 pl-6 pr-2 text-sm text-gray-300 hover:text-gold hover:bg-navy-light rounded-md transition-colors"
                              data-testid="nav-mobile-credit-signup-link"
                            >
                              Credit Services Sign Up
                              <ChevronRight className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => { setOpen(false); window.open(CREDIT_CLIENTS_LOGIN_URL, "_blank", "noopener"); }}
                              className="flex items-center justify-between text-left py-2.5 pl-6 pr-2 text-sm text-gray-300 hover:text-gold hover:bg-navy-light rounded-md transition-colors"
                              data-testid="nav-mobile-credit-clients-login-link"
                            >
                              Credit Clients Login
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  {user ? (
                    <>
                      <div className="mt-4 flex items-center gap-3 px-2 py-3 bg-navy-light rounded-md">
                        <Avatar
                          name={user.name}
                          src={user.avatar}
                          className="h-9 w-9 rounded-full bg-gold text-navy text-sm font-semibold"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-white truncate">{user.name}</div>
                          <div className="text-xs text-gray-300 truncate">{user.email}</div>
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          setOpen(false);
                          navigate("/portal");
                        }}
                        className="mt-3 bg-gold hover:bg-gold-dark text-navy font-semibold"
                      >
                        <LayoutDashboard className="h-4 w-4 mr-1.5" /> Client Portal
                      </Button>
                      <Button
                        onClick={() => {
                          logout();
                          setOpen(false);
                        }}
                        variant="outline"
                        className="mt-2 border-gray-500 text-white hover:bg-white hover:text-navy bg-transparent"
                      >
                        <LogOut className="h-4 w-4 mr-1.5" /> Log out
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => openModal("signup")}
                      className="mt-4 bg-gold hover:bg-gold-dark text-navy font-semibold"
                    >
                      <UserPlus className="h-4 w-4 mr-1.5" /> Create Account / Sign In
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      <AuthModal open={modalOpen} onOpenChange={setModalOpen} defaultTab={modalTab} />
    </header>
  );
}
